import { Node, mergeAttributes } from "@tiptap/core";

export interface InlineButtonOptions {
  HTMLAttributes: Record<string, any>;
  onEditButton?: (attrs: InlineButtonAttrs, pos: number) => void;
}

export interface InlineButtonAttrs {
  label: string;
  href: string;
  variant: string;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    inlineButton: {
      insertInlineButton: (attrs: InlineButtonAttrs) => ReturnType;
      updateInlineButton: (pos: number, attrs: InlineButtonAttrs) => ReturnType;
    };
  }
}

export const InlineButton = Node.create<InlineButtonOptions>({
  name: "inlineButton",
  group: "inline",
  inline: true,
  atom: true,

  addOptions() {
    return {
      HTMLAttributes: { class: "tiptap-inline-button" },
      onEditButton: undefined,
    };
  },

  addAttributes() {
    return {
      label: { default: "Przycisk" },
      href: { default: "#" },
      variant: { default: "primary" },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'button[data-type="inline-button"]',
        getAttrs: (el) => ({
          label: (el as HTMLElement).textContent,
          href: (el as HTMLElement).getAttribute("data-href"),
          variant: (el as HTMLElement).getAttribute("data-variant") || "primary",
        }),
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "button",
      mergeAttributes(this.options.HTMLAttributes, {
        "data-type": "inline-button",
        "data-href": HTMLAttributes.href,
        "data-variant": HTMLAttributes.variant,
        contenteditable: "false",
      }),
      HTMLAttributes.label,
    ];
  },

  addCommands() {
    return {
      insertInlineButton:
        (attrs) =>
        ({ commands }) => {
          return commands.insertContent({ type: this.name, attrs });
        },
      updateInlineButton:
        (pos, attrs) =>
        ({ tr, dispatch }) => {
          if (dispatch) {
            tr.setNodeMarkup(pos, undefined, attrs);
          }
          return true;
        },
    };
  },

  addNodeView() {
    return ({ node, getPos, editor }) => {
      const dom = document.createElement("button");
      dom.className = "tiptap-inline-button";
      dom.setAttribute("data-type", "inline-button");
      dom.setAttribute("data-href", node.attrs.href);
      dom.setAttribute("data-variant", node.attrs.variant);
      dom.setAttribute("contenteditable", "false");
      dom.textContent = node.attrs.label;

      dom.addEventListener("dblclick", (e) => {
        e.preventDefault();
        e.stopPropagation();
        const pos = typeof getPos === "function" ? getPos() : undefined;
        if (pos !== undefined && this.options.onEditButton) {
          this.options.onEditButton(
            {
              label: node.attrs.label,
              href: node.attrs.href,
              variant: node.attrs.variant,
            },
            pos
          );
        }
      });

      return {
        dom,
        update: (updatedNode) => {
          if (updatedNode.type.name !== this.name) {
            return false;
          }
          dom.textContent = updatedNode.attrs.label;
          dom.setAttribute("data-href", updatedNode.attrs.href);
          dom.setAttribute("data-variant", updatedNode.attrs.variant);
          return true;
        },
      };
    };
  },
});
